import React, { useState, useEffect } from 'react';
import { ArrowUpDown, Copy, CheckSquare, Sun, Moon } from 'lucide-react';

const LANGUAGES = {
  en: { name: 'English', flag: '🇬🇧' },
  sk: { name: 'Slovak', flag: '🇸🇰' }
};

const CURRENCIES = ['EUR', 'USD', 'CZK'];

const CRYPTOCURRENCIES = ['BTC', 'LTC', 'XMR', 'XRP', 'USDT'];

const translations = {
  en: {
    title: 'Crypto Calculator',
    amountFiat: 'Amount in',
    amountCrypto: 'Amount of',
    fee: 'Purchase fee',
    resultCrypto: 'Crypto to receive',
    resultFiat: 'Amount to receive',
    copy: 'Copy',
    copied: 'Copied!',
    currentPrice: 'Current Price:',
    buy: 'Buy',
    sell: 'Sell',
    loading: 'Loading...',
    error: 'Error fetching price',
    qrCodeTitle: 'Support the developer',
    close: 'Close',
    selectCrypto: 'Select cryptocurrency',
    networkFee: 'Current network fee (medium priority):',
    notAvailable: 'N/A',
    satPerByte: 'sat/vB',
    toggleDarkMode: 'Toggle dark mode'
  },
  sk: {
    title: 'Krypto Kalkulačka',
    amountFiat: 'Suma v',
    amountCrypto: 'Množstvo',
    fee: 'Poplatok za nákup',
    resultCrypto: 'Krypto na prijatie',
    resultFiat: 'Suma na prijatie',
    copy: 'Kopírovať',
    copied: 'Skopírované!',
    currentPrice: 'Aktuálna cena:',
    buy: 'Nákup',
    sell: 'Predaj',
    loading: 'Načítavam...',
    error: 'Chyba pri načítaní ceny',
    qrCodeTitle: 'Podpor vývojára',
    close: 'Zavrieť',
    selectCrypto: 'Vyberte kryptomenu',
    networkFee: 'Aktuálny poplatok na sieti (stredná priorita):',
    notAvailable: 'Nedostupné',
    satPerByte: 'sat/vB',
    toggleDarkMode: 'Prepnúť tmavý režim'
  }
};

const App = () => {
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('USD');
  const [cryptoCurrency, setCryptoCurrency] = useState('BTC');
  const [cryptoPrices, setCryptoPrices] = useState({ 
    BTC: { USD: 0, EUR: 0, CZK: 0 }, 
    LTC: { USD: 0, EUR: 0, CZK: 0 }, 
    XMR: { USD: 0, EUR: 0, CZK: 0 },
    XRP: { USD: 0, EUR: 0, CZK: 0 },
    USDT: { USD: 0, EUR: 0, CZK: 0 }
  });
  const [amount, setAmount] = useState(100);
  const [fee, setFee] = useState(3);
  const [result, setResult] = useState(0);
  const [buyMode, setBuyMode] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [networkFee, setNetworkFee] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  const fetchNetworkFee = async (crypto) => {
    if (crypto !== 'BTC') return null;
    try {
      const response = await fetch('https://mempool.space/api/v1/fees/recommended');
      const data = await response.json();
      return data.halfHourFee;
    } catch (error) {
      console.error('Error fetching network fee:', error);
      return null;
    }
  };

  const fetchCryptoPrices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [priceResponse, btcFee] = await Promise.all([
        fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,litecoin,monero,ripple,tether&vs_currencies=usd,eur,czk'),
        fetchNetworkFee('BTC')
      ]);

      if (!priceResponse.ok) {
        throw new Error('Network response was not ok');
      }

      const priceData = await priceResponse.json();

      setCryptoPrices({
        BTC: { USD: priceData.bitcoin.usd, EUR: priceData.bitcoin.eur, CZK: priceData.bitcoin.czk },
        LTC: { USD: priceData.litecoin.usd, EUR: priceData.litecoin.eur, CZK: priceData.litecoin.czk },
        XMR: { USD: priceData.monero.usd, EUR: priceData.monero.eur, CZK: priceData.monero.czk },
        XRP: { USD: priceData.ripple.usd, EUR: priceData.ripple.eur, CZK: priceData.ripple.czk },
        USDT: { USD: priceData.tether.usd, EUR: priceData.tether.eur, CZK: priceData.tether.czk }
      });

      setNetworkFee(btcFee);
    } catch (error) {
      console.error('Error fetching crypto data:', error);
      setError('Error fetching data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCryptoPrices();
    const interval = setInterval(fetchCryptoPrices, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    calculateResult();
  }, [amount, fee, cryptoPrices, buyMode, currency, cryptoCurrency]);

  const calculateResult = () => {
    const price = cryptoPrices[cryptoCurrency][currency];
    if (buyMode) {
      const cryptoAmount = amount / price;
      const feeAmount = cryptoAmount * (fee / 100);
      setResult(cryptoAmount - feeAmount);
    } else {
      const fiatAmount = amount * price;
      const feeAmount = fiatAmount * (fee / 100);
      setResult(fiatAmount - feeAmount);
    }
  };

  const handleCopy = () => {
    const textToCopy = buyMode ? result.toFixed(8) : formatCurrency(result);
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    if (newLanguage === 'sk') {
      setCurrency('EUR');
    }
  };

  const t = translations[language];

  const formatCurrency = (value, currencyOverride = null) => {
    const curr = currencyOverride || currency;
    return `${value.toFixed(2)} ${curr}`;
  };

  const getNetworkFeeDisplay = () => {
    if (networkFee === null || !cryptoPrices.BTC[currency]) return t.notAvailable;
    const feeInSats = networkFee * 250; // Assuming 250 bytes for an average transaction
    const feeInBTC = feeInSats / 100000000; // Convert sats to BTC
    const feeInFiat = feeInBTC * cryptoPrices.BTC[currency];
    return `${networkFee} ${t.satPerByte} (${formatCurrency(feeInFiat)})`;
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleBuyNowClick = () => {
    window.location.href = 'https://btcpay.skusimto.org/apps/49eJPgupqj6BijhtiwTpFyXREuA3/pos';
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh', 
      backgroundColor: darkMode ? '#1a202c' : '#f7fafc', 
      padding: '20px', 
      width: '100%',
      maxWidth: '100%',  // Zabezpečenie, aby nič nepresahovalo
      overflowX: 'hidden'  // Explicitne skrytie horizontálneho posúvania
    }}>
      <div style={{ 
        width: '100%', 
        maxWidth: '500px', 
        margin: '0 auto', 
        padding: '24px', 
        borderRadius: '12px', 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
        backgroundColor: darkMode ? '#2d3748' : 'white', 
        color: darkMode ? 'white' : 'black',
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <select 
            value={language} 
            onChange={(e) => changeLanguage(e.target.value)} 
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#4a5568' : '#f0f0f0',
              color: darkMode ? 'white' : 'black',
              border: '1px solid #ccc',
              fontSize: '14px',
              maxWidth: '100%'
            }}
          >
            {Object.entries(LANGUAGES).map(([key, value]) => (
              <option key={key} value={key}>{value.flag} {value.name}</option>
            ))}
          </select>

          <select 
            value={currency} 
            onChange={(e) => setCurrency(e.target.value)} 
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#4a5568' : '#f0f0f0',
              color: darkMode ? 'white' : 'black',
              border: '1px solid #ccc',
              fontSize: '14px',
              maxWidth: '100%'
            }}
          >
            {CURRENCIES.map((curr) => (
              <option key={curr} value={curr}>{curr}</option>
            ))}
          </select>

          <button onClick={toggleDarkMode} style={{
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: darkMode ? '#f6e05e' : '#2d3748',
            color: darkMode ? 'black' : 'white',
            border: 'none',
            cursor: 'pointer'
          }}>
            {darkMode ? <Sun style={{width: '16px', height: '16px'}} /> : <Moon style={{width: '16px', height: '16px'}} />}
          </button>
        </div>

        <h1 style={{ fontSize: '24px', fontWeight: 'bold', textAlign: 'center', marginBottom: '20px' }}>{t.title}</h1>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            {t.selectCrypto}
          </label>
          <select 
            value={cryptoCurrency} 
            onChange={(e) => setCryptoCurrency(e.target.value)} 
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#4a5568' : '#f0f0f0',
              color: darkMode ? 'white' : 'black',
              border: '1px solid #ccc',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          >
            {CRYPTOCURRENCIES.map((crypto) => (
              <option key={crypto} value={crypto}>{crypto}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            {buyMode ? `${t.amountFiat} ${currency}` : `${t.amountCrypto} ${cryptoCurrency}`}
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value))}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#4a5568' : '#f0f0f0',
              color: darkMode ? 'white' : 'black',
              border: '1px solid #ccc',
              fontSize: '14px',
              boxSizing: 'border-box',
              marginTop: '8px'
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <button 
            onClick={() => setBuyMode(!buyMode)} 
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#4c51bf' : '#3182ce',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              maxWidth: '100%'
            }}
          >
            <ArrowUpDown style={{ marginRight: '8px', width: '16px', height: '16px' }} />
            {buyMode ? t.sell : t.buy}
          </button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t.fee} (%)</label>
          <input
            type="range"
            min="0"
            max="30"
            step="0.1"
            value={fee}
            onChange={(e) => setFee(parseFloat(e.target.value))}
            style={{
              width: '100%'
            }}
          />
          <div style={{ textAlign: 'center', fontSize: '14px' }}>{fee.toFixed(1)}%</div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            {buyMode ? `${t.resultCrypto} (${cryptoCurrency})` : `${t.resultFiat} (${currency})`}
          </label>
          <div style={{ display: 'flex' }}>
            <input
              type="text"
              value={buyMode ? result.toFixed(8) : formatCurrency(result)}
              readOnly
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '8px 0 0 8px',
                backgroundColor: darkMode ? '#4a5568' : '#f0f0f0',
                color: darkMode ? 'white' : 'black',
                border: '1px solid #ccc',
                borderRight: 'none',
                fontSize: '14px'
              }}
            />
            <button 
              onClick={handleCopy} 
              style={{
                padding: '12px',
                borderRadius: '0 8px 8px 0',
                backgroundColor: darkMode ? '#4c51bf' : '#3182ce',
                color: 'white',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {isCopied ? (
                <CheckSquare style={{ width: '16px', height: '16px' }} />
              ) : (
                <Copy style={{ width: '16px', height: '16px' }} />
              )}
            </button>
          </div>
        </div>

        {cryptoCurrency === 'BTC' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              {t.networkFee} {getNetworkFeeDisplay()}
            </label>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
          <button onClick={handleBuyNowClick} style={{
            width: '100%',
            minHeight: '57px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#0f3b21',
            cursor: 'pointer',
            color: '#fff',
            fontSize: '16px',
            padding: '16px'
          }}>
            Donate
          </button>
        </div>

        {isCopied && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            backgroundColor: '#48bb78',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px'
          }}>
            {t.copied}
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
